import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import Spinner from '../../components/Spinner';
import { 
    PlusIcon, 
    PencilIcon, 
    TrashIcon, 
    EyeIcon,
    ClockIcon,
    TagIcon,
    NewspaperIcon,
    BeakerIcon,
    InformationCircleIcon,
    SparklesIcon,
    FunnelIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const ArticleListPage = () => {
    const [articles, setArticles] = useState([]);
    const [filteredArticles, setFilteredArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterPageType, setFilterPageType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const { token } = useContext(AuthContext);

    const fetchArticles = useCallback(async () => {
        setLoading(true);
        try {
            const config = { headers: { 'x-auth-token': token } };
            const res = await axios.get(`${API_BASE_URL}/api/articles/editor-panel`, config);
            setArticles(res.data);
            setFilteredArticles(res.data);
        } catch (err) {
            toast.error("Failed to load articles.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) fetchArticles();
    }, [token, fetchArticles]);

    // Filter articles based on search and filters
    useEffect(() => {
        let filtered = articles;

        // Search filter
        if (searchQuery) {
            filtered = filtered.filter(article =>
                article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                article.category?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Page type filter
        if (filterPageType !== 'all') {
            filtered = filtered.filter(article => article.pageType === filterPageType);
        }

        // Status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(article => article.status === filterStatus);
        }

        setFilteredArticles(filtered);
    }, [searchQuery, filterPageType, filterStatus, articles]);

    const handleDelete = async (articleId) => {
        if (window.confirm('Are you sure you want to permanently delete this article?')) {
            const loadingToast = toast.loading('Deleting article...');
            try {
                const config = { headers: { 'x-auth-token': token } };
                await axios.delete(`${API_BASE_URL}/api/articles/${articleId}`, config);
                toast.success('Article deleted successfully!', { id: loadingToast });
                fetchArticles();
            } catch (err) {
                toast.error(err.response?.data?.msg || 'Failed to delete article.', { id: loadingToast });
            }
        }
    };

    const getPageTypeIcon = (pageType) => {
        switch (pageType) {
            case 'News': return <NewspaperIcon className="h-5 w-5" />;
            case 'Research': return <BeakerIcon className="h-5 w-5" />;
            case 'Info': return <InformationCircleIcon className="h-5 w-5" />;
            default: return <NewspaperIcon className="h-5 w-5" />;
        }
    };

    const getPageTypeColor = (pageType) => {
        switch (pageType) {
            case 'News': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
            case 'Research': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
            case 'Info': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
            default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300';
        }
    };

    const stats = {
        total: articles.length,
        published: articles.filter(a => a.status === 'published').length,
        draft: articles.filter(a => a.status === 'draft').length,
        news: articles.filter(a => a.pageType === 'News').length,
        research: articles.filter(a => a.pageType === 'Research').length,
        info: articles.filter(a => a.pageType === 'Info').length
    };

    if (loading) return <Spinner />;

    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="pb-8"
        >
            {/* Header */}
            <div className="card p-6 mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">Content Management</h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Manage all your articles, news, and research content
                        </p>
                    </div>
                    <Link to="/editor/add-article" className="btn btn-primary flex items-center gap-2 w-fit">
                        <PlusIcon className="h-5 w-5" />
                        Create New Article
                    </Link>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <div className="card p-4">
                    <div className="text-gray-600 dark:text-gray-400 text-sm mb-1">Total</div>
                    <div className="text-2xl font-bold">{stats.total}</div>
                </div>
                <div className="card p-4">
                    <div className="text-green-600 dark:text-green-400 text-sm mb-1">Published</div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.published}</div>
                </div>
                <div className="card p-4">
                    <div className="text-yellow-600 dark:text-yellow-400 text-sm mb-1">Drafts</div>
                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.draft}</div>
                </div>
                <div className="card p-4">
                    <div className="text-blue-600 dark:text-blue-400 text-sm mb-1 flex items-center gap-1">
                        <NewspaperIcon className="h-4 w-4" /> News
                    </div>
                    <div className="text-2xl font-bold">{stats.news}</div>
                </div>
                <div className="card p-4">
                    <div className="text-purple-600 dark:text-purple-400 text-sm mb-1 flex items-center gap-1">
                        <BeakerIcon className="h-4 w-4" /> Research
                    </div>
                    <div className="text-2xl font-bold">{stats.research}</div>
                </div>
                <div className="card p-4">
                    <div className="text-green-600 dark:text-green-400 text-sm mb-1 flex items-center gap-1">
                        <InformationCircleIcon className="h-4 w-4" /> Info
                    </div>
                    <div className="text-2xl font-bold">{stats.info}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="card p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search articles by title, excerpt, or category..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="input-field pl-10"
                            />
                        </div>
                    </div>

                    {/* Page Type Filter */}
                    <select
                        value={filterPageType}
                        onChange={(e) => setFilterPageType(e.target.value)}
                        className="input-field md:w-48"
                    >
                        <option value="all">All Types</option>
                        <option value="News">📰 News</option>
                        <option value="Research">🔬 Research</option>
                        <option value="Info">ℹ️ Info</option>
                    </select>

                    {/* Status Filter */}
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="input-field md:w-48"
                    >
                        <option value="all">All Status</option>
                        <option value="published">✅ Published</option>
                        <option value="draft">📝 Draft</option>
                    </select>
                </div>

                {/* Active Filters Display */}
                {(searchQuery || filterPageType !== 'all' || filterStatus !== 'all') && (
                    <div className="mt-4 flex items-center gap-2 text-sm">
                        <FunnelIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-600 dark:text-gray-400">
                            Showing {filteredArticles.length} of {articles.length} articles
                        </span>
                        {searchQuery && (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                Search: "{searchQuery}"
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Articles Grid */}
            {filteredArticles.length === 0 ? (
                <div className="card p-12 text-center">
                    <div className="text-gray-400 dark:text-gray-600 mb-4">
                        <NewspaperIcon className="h-16 w-16 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold mb-2">No articles found</h3>
                        <p>
                            {searchQuery || filterPageType !== 'all' || filterStatus !== 'all'
                                ? 'Try adjusting your filters or search query'
                                : 'Get started by creating your first article'}
                        </p>
                    </div>
                    {!searchQuery && filterPageType === 'all' && filterStatus === 'all' && (
                        <Link to="/editor/add-article" className="btn btn-primary inline-flex items-center gap-2 mt-4">
                            <PlusIcon className="h-5 w-5" />
                            Create Your First Article
                        </Link>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredArticles.map((article) => (
                        <motion.div
                            key={article._id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="card overflow-hidden group hover:shadow-lg transition-shadow"
                        >
                            {/* Cover Image */}
                            <div className="relative h-48 overflow-hidden bg-gray-200 dark:bg-gray-800">
                                <img
                                    src={article.coverImageUrl}
                                    alt={article.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                                    }}
                                />
                                {/* Badges */}
                                <div className="absolute top-3 left-3 flex gap-2">
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getPageTypeColor(article.pageType)}`}>
                                        {getPageTypeIcon(article.pageType)}
                                        {article.pageType}
                                    </span>
                                    {article.featured && (
                                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 flex items-center gap-1">
                                            <SparklesIcon className="h-4 w-4" />
                                            Featured
                                        </span>
                                    )}
                                </div>
                                <div className="absolute top-3 right-3">
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                        article.status === 'published'
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                                    }`}>
                                        {article.status === 'published' ? '✅ Published' : '📝 Draft'}
                                    </span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-5">
                                {/* Title */}
                                <h3 className="text-xl font-bold mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                    {article.title}
                                </h3>

                                {/* Excerpt */}
                                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                                    {article.excerpt}
                                </p>

                                {/* Meta Info */}
                                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                                    <div className="flex items-center gap-1">
                                        <ClockIcon className="h-4 w-4" />
                                        {article.readTime || 5} min
                                    </div>
                                    {article.views !== undefined && (
                                        <div className="flex items-center gap-1">
                                            <EyeIcon className="h-4 w-4" />
                                            {article.views}
                                        </div>
                                    )}
                                    {article.category && (
                                        <div className="flex items-center gap-1">
                                            <TagIcon className="h-4 w-4" />
                                            {article.category}
                                        </div>
                                    )}
                                </div>

                                {/* Tags */}
                                {article.tags && article.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {article.tags.slice(0, 3).map((tag, index) => (
                                            <span
                                                key={index}
                                                className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded"
                                            >
                                                #{tag}
                                            </span>
                                        ))}
                                        {article.tags.length > 3 && (
                                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded">
                                                +{article.tags.length - 3}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Author & Date */}
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                                    By {article.author?.name || 'Unknown'} • {new Date(article.createdAt).toLocaleDateString()}
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2">
                                    <Link
                                        to={`/editor/edit-article/${article._id}`}
                                        className="flex-1 btn btn-secondary text-sm !py-2 flex items-center justify-center gap-2"
                                    >
                                        <PencilIcon className="h-4 w-4" />
                                        Edit
                                    </Link>
                                    <button
                                        onClick={() => handleDelete(article._id)}
                                        className="btn btn-danger text-sm !py-2 flex items-center justify-center gap-2"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </motion.div>
    );
};

export default ArticleListPage;